import { MytoursApiFetchParamCreator, Configuration } from 'mytours-read-api-typescript'
import STQRYBunnyReadApi, { ManifestFull } from 'stqry-bunny-read-api'

import { ItemType, VersionedManifest } from './registry'

/**
 * Where a download reads content from. Two projects read two different places: one with the publishing
 * model on reads the published subtree on the CDN, one without it reads the direct API — the same split
 * `shouldUsePublishedContent` already makes for browsing, which is why a download that only ever spoke
 * the published shape could not work on the projects that are the large majority.
 *
 * The four members are the whole surface a download needs: two manifests to learn what exists, and two
 * URL builders to address it. Everything below media is shared, because a media file lives on the same
 * CDN either way.
 */
export interface ContentLocator {
	/** Where this locator's documents are read from, and so the only origin credentials may be sent to. */
	contentOrigin: string
	fetchCollectionManifest(accountId: number, collectionId: number): Promise<VersionedManifest>
	fetchManifestFull(accountId: number): Promise<ManifestFull>
	resourceUrls(accountId: number, type: ItemType, id: number, version: number, language: string): string[]
	collectionIndexUrls(
		accountId: number,
		collectionId: number,
		version: number,
		language: string,
		index: 'items' | 'triggers',
	): string[]
	/** The collection with items and screens inline, which the single page layout reads. Empty where its own document
	 * already carries them. */
	collectionFullUrls(accountId: number, collectionId: number, version: number, language: string): string[]
}

/**
 * The published folder each section lives under. Every one is `<folder>/<hashid>/metadata{-locale}.json`;
 * forms are the only section whose folder is not its own name.
 */
const PUBLISHED_FOLDERS: Partial<Record<ItemType, string>> = {
	collections: 'collections',
	screens: 'screens',
	media_items: 'media_items',
	quizzes: 'quizzes',
	maps: 'maps',
	forms: 'crm/forms',
	badge_groups: 'badge_groups',
}

/** A project with the publishing model on: the published subtree, addressed by hashid. */
export const publishedLocator = (api: STQRYBunnyReadApi): ContentLocator => ({
	contentOrigin: new URL(api.host).origin,
	fetchCollectionManifest: (accountId, collectionId) =>
		api.fetchCollectionManifest(accountId, collectionId) as Promise<VersionedManifest>,
	fetchManifestFull: accountId => api.fetchManifestFull(accountId),
	resourceUrls: (accountId, type, id, version, language) =>
		api.resourceUrls(accountId, PUBLISHED_FOLDERS[type] ?? type, id, version, language),
	collectionIndexUrls: (accountId, collectionId, version, language, index) =>
		api.collectionIndexUrls(accountId, collectionId, version, language, index),
	// A single page collection is published with its items inline, under the same key as its metadata.
	collectionFullUrls: () => [],
})

/** The direct-API path segment for each section. Unlike the published folders, forms are plain `forms`. */
const DIRECT_SEGMENTS: Partial<Record<ItemType, string>> = {
	collections: 'collections',
	screens: 'screens',
	media_items: 'media_items',
	quizzes: 'quizzes',
	maps: 'maps',
	forms: 'forms',
	badge_groups: 'badge_groups',
}

export interface DirectLocatorOptions {
	baseUrl: string
	devicePlatform: string
	deviceUdid: string
	/** The project credentials every direct read carries; omitted in the URL tests, which fetch nothing. */
	projectId?: string
	apiKey?: string
	fetch?: typeof globalThis.fetch
}

/** What the reader sends on a collection items request, so a stored body lands under the key it asks by. */
const ITEMS_PER_PAGE = 1000

/**
 * A project with the publishing model off: the direct API, addressed by raw id with the version in the
 * path. That the version is in the path rather than a `?t=` is what keeps these immutable, so the worker
 * can serve them cache-first exactly as it does the published ones.
 *
 * URLs come from the generated client's own parameter creator rather than being assembled here. The
 * worker caches by URL, so a download that spelled a query string differently from the reader would
 * store every document under a key nothing ever asks for — a download that silently caches nothing. Using
 * one builder for both makes that class of drift impossible rather than merely unlikely.
 */
export const directLocator = ({
	baseUrl,
	devicePlatform,
	deviceUdid,
	projectId,
	apiKey,
	fetch: fetcher = globalThis.fetch,
}: DirectLocatorOptions): ContentLocator => {
	const platform = devicePlatform as 'mobile_web'
	const params = MytoursApiFetchParamCreator(new Configuration({ username: projectId, password: apiKey }))
	const urlFor = (args: { url: string }): string => `${baseUrl}${args.url}`

	const readManifest = async <T>(path: string): Promise<T> => {
		const response = await fetcher(`${baseUrl}${path}`, { headers: authHeaders(projectId, apiKey) })
		if (!response.ok) {
			throw new Error(`The manifest at ${path} could not be read (${response.status})`)
		}

		return (await response.json()) as T
	}

	return {
		contentOrigin: new URL(baseUrl).origin,
		fetchCollectionManifest: (_accountId, collectionId) =>
			readManifest<VersionedManifest>(params.collectionManifest(platform, collectionId, 0, deviceUdid).url),
		// `full` is what makes the project manifest carry the `cached` flags that name the offline set —
		// without it the same endpoint answers with the plain manifest and every item reads as unflagged.
		fetchManifestFull: _accountId =>
			readManifest<ManifestFull>(params.projectManifest(platform, deviceUdid, 'true').url),
		resourceUrls: (_accountId, type, id, version, language) => [
			urlFor(showParams(params, DIRECT_SEGMENTS[type] ?? type, platform, id, version, language, deviceUdid)),
		],
		collectionIndexUrls: (_accountId, collectionId, version, language, index) => [
			urlFor(
				index === 'items'
					? params.collectionItemsIndex(
							platform,
							collectionId,
							version,
							language,
							deviceUdid,
							undefined,
							ITEMS_PER_PAGE,
						)
					: params.collectionTriggersIndex(collectionId, version, language),
			),
		],
		collectionFullUrls: (_accountId, collectionId, version, language) => [
			urlFor(params.collectionsShow(platform, collectionId, version, language, deviceUdid, 'true')),
		],
	}
}

/**
 * The per-section show endpoint. They share one path shape but not one signature — `formShow` and the
 * trigger index take no device parameters — so each is named rather than called through an index.
 */
const showParams = (
	params: ReturnType<typeof MytoursApiFetchParamCreator>,
	segment: string,
	platform: 'mobile_web',
	id: number,
	version: number,
	language: string,
	deviceUdid: string,
): { url: string } => {
	switch (segment) {
		case 'collections':
			return params.collectionsShow(platform, id, version, language, deviceUdid)
		case 'screens':
			return params.screensShow(platform, id, version, language, deviceUdid)
		case 'media_items':
			return params.mediaItemsShow(platform, id, version, language, deviceUdid)
		case 'quizzes':
			return params.quizzesShow(platform, id, version, language, deviceUdid)
		// The generated `mapsShow` takes only an id and leaves `{language}` and `{version}` unsubstituted,
		// so it cannot address the endpoint its own template describes. Nothing reads a map document on the
		// web — the reader fetches map routes, not maps — so this exists to keep the manifest's remainder
		// addressable rather than to serve a read, and is spelled out here rather than left malformed.
		case 'maps':
			return { url: `/api/v3/maps/${id}/${encodeURIComponent(language)}/${version}` }
		case 'badge_groups':
			return params.badgeGroupsShow(platform, id, version, language, deviceUdid)
		default:
			return params.formShow(id, version, language)
	}
}

/**
 * The queue's fetch. Content and media come off different origins with different rules: both content
 * hosts refuse an unauthenticated read, while the media hosts answer a preflight with a header allowlist
 * that does not include `Authorization` — so a media request carrying credentials is refused by CORS
 * before it leaves the browser, and the download fails on every image and every audio file. Credentials
 * therefore go to the content origin and nowhere else.
 */
export const contentFetch =
	(contentOrigin: string, { projectId, apiKey }: { projectId: string; apiKey: string }, send = globalThis.fetch) =>
	(url: string): Promise<Response> => {
		const sameOrigin = url.startsWith(`${contentOrigin}/`)

		return sameOrigin ? send(url, { headers: authHeaders(projectId, apiKey) }) : send(url)
	}

/** Basic auth plus the project-type header, which the content hosts require on every read. */
export const authHeaders = (projectId?: string, apiKey?: string): Record<string, string> => {
	const headers: Record<string, string> = { 'Stqry-Project-Type': 'mobile_web' }
	if (projectId !== undefined && apiKey !== undefined) {
		headers.Authorization = `Basic ${btoa(`${projectId}:${apiKey}`)}`
	}

	return headers
}
